import * as React from 'react';
import { Modal } from 'antd';
import FSimpleBar from '@/components/FSimpleBar';
import styles from './index.less';

export type WinnerAnnouncementResourceItem = {
  resourceID?: string;
  resourceCover: string;
  resourceTitle: string;
  avatar: string;
  username: string;
};

export interface WinnerAnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  /** 最佳创意奖，最多 3 条 */
  bestCreative?: WinnerAnnouncementResourceItem[];
  /** 优秀入围奖，最多 9 条 */
  excellent?: WinnerAnnouncementResourceItem[];
  /** 全程参与奖用户名 */
  fullParticipationUsernames?: string[];
  /** 新春达人奖用户名 */
  springExpertUsernames?: string[];
  onResourceClick?: (resourceID: string) => void;
}

function CloseIcon() {
  return (
    <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden>
      <path
        d='M5 5L15 15M15 5L5 15'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
      />
    </svg>
  );
}

function ResourceCard({
  item,
  onResourceClick,
}: {
  item: WinnerAnnouncementResourceItem;
  onResourceClick?: (resourceID: string) => void;
}) {
  const hasId = Boolean(item.resourceID);
  const handleWorkClick = () => {
    if (item.resourceID) {
      onResourceClick?.(item.resourceID);
    }
  };

  return (
    <div
      className={[styles.resourceCard, hasId ? styles.clickableCard : ''].join(' ')}
      onClick={hasId ? handleWorkClick : undefined}
      role={hasId ? 'button' : undefined}
      tabIndex={hasId ? 0 : -1}
    >
      <div
        className={styles.cover}
        style={
          item.resourceCover
            ? {
                backgroundImage: 'url(' + item.resourceCover + ')',
              }
            : undefined
        }
      >
        {item.resourceCover ? (
          <>
            <div />
            <article style={{ backgroundImage: 'url(' + item.resourceCover + ')' }} />
          </>
        ) : (
          <div className={styles.coverPlaceholder} />
        )}
      </div>
      <div className={styles.resourceTitleRow}>
        <span className={[styles.resourceTitle, hasId ? styles.clickable : ''].join(' ')}>
          {item.resourceTitle}
        </span>
      </div>
      <div className={styles.authorRow}>
        <div className={styles.avatar}>
          {item.avatar ? <img src={item.avatar} alt='' /> : null}
        </div>
        <span className={styles.username}>{item.username}</span>
      </div>
    </div>
  );
}

export default function WinnerAnnouncementModal({
  open,
  onClose,
  bestCreative,
  excellent,
  fullParticipationUsernames,
  springExpertUsernames,
  onResourceClick,
}: WinnerAnnouncementModalProps) {
  const bestList = (bestCreative ?? []).slice(0, 3);
  const excellentList = (excellent ?? []).slice(0, 9);
  const fullNames = (fullParticipationUsernames ?? []).filter(Boolean);
  const expertNames = (springExpertUsernames ?? []).filter(Boolean);

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      closable={false}
      width={1000}
      centered
      destroyOnClose
      style={{ borderRadius: 10, overflow: 'hidden' }}
      onCancel={onClose}
      styles={{
        body: {
          padding: 0,
          height: '100%',
          width: '100%',
        },
        content: {
          padding: 0,
          height: 'calc(100vh - 200px)',
          width: '100%',
        },
      }}
    >
      <div className={styles.root}>
        <div className={styles.header}>
          <span className={styles.title}>获奖公示</span>
          <button type='button' className={styles.close} onClick={onClose} aria-label='关闭'>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.simpleBarHost}>
          <FSimpleBar>
            <div className={styles.scrollInner}>
              <div className={styles.section}>
                <div className={styles.sectionHead}>
                  <span className={styles.sectionTitle}>最佳创意奖</span>
                  <span className={styles.sectionSubtitle}>——    每名奖励 ¥500    ——</span>
                </div>
                <div className={styles.row3}>
                  {bestList.map((item, index) => (
                    <ResourceCard
                      key={`best-${index}`}
                      item={item}
                      onResourceClick={onResourceClick}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionHead}>
                  <span className={styles.sectionTitle}>优秀入围奖</span>
                  <span className={styles.sectionSubtitle}>——    每名奖励 ¥200    ——</span>
                </div>
                <div className={styles.rowWrap}>
                  {excellentList.map((item, index) => (
                    <ResourceCard
                      key={`exc-${index}`}
                      item={item}
                      onResourceClick={onResourceClick}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionHead}>
                  <span className={styles.sectionTitle}>全程参与奖</span>
                  <span className={styles.sectionSubtitle}>——    每名奖励 ¥50    ——</span>
                </div>
                <div className={styles.pillRow}>
                  {fullNames.map((name, index) => (
                    <span key={`full-${index}`} className={styles.pill}>
                      {name}
                    </span>
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionHead}>
                  <span className={styles.sectionTitle}>新春达人奖</span>
                  <span className={styles.sectionSubtitle}>——    每名奖励 ¥100    ——</span>
                </div>
                <div className={styles.pillRow}>
                  {expertNames.map((name, index) => (
                    <span key={`exp-${index}`} className={styles.pill}>
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </FSimpleBar>
        </div>
      </div>
    </Modal>
  );
}
